import React, { useEffect, useRef, useState } from "react";
import Icon from "./icons";
import Avatar from "./avatar";

// Closes a popover on Escape or a click outside it (and outside its opener, by class).
const useDismiss = (ref, onClose, openerClass) => useEffect(() => {
  const onKey = e => { if (e.key === 'Escape') onClose() }
  const onDown = e => { if (ref.current && !ref.current.contains(e.target) && !(openerClass && e.target.closest(`.${openerClass}`))) onClose() }
  document.addEventListener('keydown', onKey)
  document.addEventListener('mousedown', onDown)
  return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
}, [])

// The open conversation's ⋯ menu (as on Android): Members (where onMembers is given), Rename (the owner),
// Archive, and Delete for the owner or Leave for a member, each asking first. The alerts inbox (alerts) has
// none of them: the server refuses renaming, deleting or archiving it (API-3922), since the phones only look
// for it among the conversations not archived. In the ARTEMIS tab's hub it also holds Read aloud
// (onReadAloud), and is all there is before a conversation is saved (conversation false).
export const SessionMenu = ({ title, isOwner, shared, alerts, session, conversation = true, onMembers, onRename, onArchive, onDelete, onReadAloud, onClose }) => {
  const ref = useRef(null)
  const [mode, setMode] = useState(null)
  const [name, setName] = useState(title || '')
  useDismiss(ref, onClose, 'artemis-session-menu-button')
  const leaving = shared && !isOwner

  return (
    <div className="artemis-popover artemis-session-menu" role="menu" aria-label="Session" ref={ref}>
      {mode === 'rename' ? (
        <form className="artemis-session-rename" onSubmit={e => { e.preventDefault(); if (name.trim()) onRename(name.trim()) }}>
          <input autoFocus value={name} maxLength={200} onChange={e => setName(e.target.value)} aria-label="Session name" />
          <button type="button" className="artemis-small-button" onClick={() => setMode(null)}>Cancel</button>
          <button type="submit" className="artemis-invite-add" disabled={!name.trim()}>Save</button>
        </form>
      ) : mode === 'delete' ? (
        <div className="artemis-session-confirm">
          <span>{leaving ? 'Leave this session? You can rejoin only with a new invite.' : shared ? 'Delete this session for everyone in it?' : session ? 'Delete this session?' : 'Delete this chat?'}</span>
          <span className="artemis-session-confirm-actions">
            <button type="button" className="artemis-small-button" onClick={() => setMode(null)}>Cancel</button>
            <button type="button" className="artemis-small-button artemis-small-button-danger" onClick={onDelete}>{leaving ? 'Leave' : 'Delete'}</button>
          </span>
        </div>
      ) : (
        <>
          {onReadAloud && <button type="button" role="menuitem" className="artemis-session-item" onClick={onReadAloud}><Icon name="speaker" size={15} />Read aloud…</button>}
          {conversation && shared && onMembers && <button type="button" role="menuitem" className="artemis-session-item" onClick={onMembers}><Icon name="people" size={15} />Members</button>}
          {conversation && !alerts && (isOwner || !shared) && <button type="button" role="menuitem" className="artemis-session-item" onClick={() => setMode('rename')}><Icon name="edit" size={15} />Rename</button>}
          {conversation && !alerts && <button type="button" role="menuitem" className="artemis-session-item" onClick={onArchive}><Icon name="archive" size={15} />Archive</button>}
          {conversation && !alerts && (
            <button type="button" role="menuitem" className="artemis-session-item artemis-session-item-danger" onClick={() => setMode('delete')}>
              <Icon name={leaving ? 'leave' : 'trash'} size={15} />{leaving ? 'Leave session' : 'Delete'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

// Who's in the session. The owner can remove anyone else; a member can leave.
export const MembersPanel = ({ members, meId, onRemove, onLeave, onClose }) => {
  const ref = useRef(null)
  const [confirm, setConfirm] = useState(null)
  useDismiss(ref, onClose, 'artemis-avatars-button')
  const me = members.find(m => String(m.UserId) === String(meId))
  const owner = !!me && me.Role === 'owner'
  return (
    <div className="artemis-popover artemis-members" role="dialog" aria-label="Members" ref={ref}>
      <span className="artemis-eyebrow">Members · {members.length}</span>
      {members.map(m => {
        const mine = String(m.UserId) === String(meId)
        const action = mine ? (owner ? null : 'leave') : (owner ? 'remove' : null)
        return (
          <div key={m.UserId} className="artemis-member-row">
            <Avatar url={m.AvatarUrl} initials={m.Initials} name={m.Name} />
            <span className="artemis-flex">
              <span className="artemis-member-name">{m.Name || 'Member'}{mine ? ' (you)' : ''}</span>
              {m.Role === 'owner' && <span className="artemis-member-role">Owner</span>}
            </span>
            {action && (confirm === m.UserId
              ? (
                <>
                  <button type="button" className="artemis-small-button" onClick={() => setConfirm(null)}>Cancel</button>
                  <button type="button" className="artemis-small-button artemis-small-button-danger"
                    onClick={() => (action === 'leave' ? onLeave() : onRemove(m.UserId))}>{action === 'leave' ? 'Leave' : 'Remove'}</button>
                </>
              )
              : <button type="button" className="artemis-small-button" onClick={() => setConfirm(m.UserId)}>{action === 'leave' ? 'Leave' : 'Remove'}</button>)}
          </div>
        )
      })}
      <div className="artemis-member-row artemis-member-ai">
        <span className="artemis-avatar artemis-avatar-ai"><Icon name="sparkle" size={13} /></span>
        <span className="artemis-flex"><span className="artemis-member-name">Artemis</span><span className="artemis-member-role">Joins when @mentioned</span></span>
      </div>
    </div>
  )
}
